import { styled } from "@linaria/react"
import { useNavigate } from "@tanstack/react-router"

import { Button } from "@/components/Button/Button.tsx"
import { Horizontal } from "@/components/Flex.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { CodeFileLight, CopyLight, EditLight, Icon16 } from "@/icons/Icons.ts"
import { copyToClipboard } from "@/lib/std.ts"
import { TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

import type { ChatMessageModel } from "@/components/Chat/ChatModel.ts"
import type { ReactNode } from "react"

export const MessageActionsPopup = (props: { model: ChatMessageModel }) => {
  const devMode = useDevMode()
  const navigate = useNavigate()

  const openDevLog = () => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, tab: TabId.Dev, filterMessageId: props.model.id })
    })
  }

  return (
    <ActionsContainer>
      {props.model.edit && (
        <MessageActionsPopupButton
          icon={<EditLight className={Icon16} />}
          title="Edit message"
          action={props.model.edit}
          testId="edit-message-action"
        />
      )}

      {props.model.text && (
        <MessageActionsPopupButton
          icon={<CopyLight className={Icon16} />}
          title="Copy text"
          action={() => copyToClipboard(props.model.text)}
          testId="copy-action"
        />
      )}

      {devMode && (
        <MessageActionsPopupButton
          icon={<CodeFileLight className={Icon16} />}
          title="View Dev Log"
          action={openDevLog}
          testId="view-dev-log-action"
        />
      )}
    </ActionsContainer>
  )
}

const MessageActionsPopupButton = (props: {
  icon: ReactNode
  action: () => void
  title: string
  testId?: string
}) => {
  return (
    <StyledMessageActionsPopupButton
      kind="icon"
      size="small"
      onClick={props.action}
      data-testid={props.testId}
    >
      <Tooltip content={props.title}>
        {props.icon}
      </Tooltip>
    </StyledMessageActionsPopupButton>
  )
}

const ActionsContainer = styled(Horizontal)`
  border-radius: var(--border-radius-8);
  padding: 4px;
  gap: 2px;

  opacity: 1;

  background: var(--background-color);

  box-shadow: 0 2px 15px 0 var(--text-color-10);
`

const StyledMessageActionsPopupButton = styled(Button)`
  border-radius: var(--border-radius-4);
  padding: 3px 4px;

  width: 24px;
  height: 22px;

  &:hover:not(:disabled) {
    background-color: var(--text-color-10);

    color: var(--text-color);
  }
`